/* ClosureTrail — one report and where it ended up, in the shell's language (13-15).
 * The report reference is the card's serif heading; each consequence is the mock's .linked
 * inset (sage-light, the relationship in teal-dark, as on Verify), so "this led to that" reads
 * the same across the tool. Purple is identity ink only (the heading, links, focus). Link and
 * closure status keep the Badge tones. */

.card {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Source header ── */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--line);
}

.headerMain {
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.label {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray);
}

.sourceRef {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 28px;
  line-height: 1.15;
  color: var(--purple-dark);
  text-decoration: none;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

a.sourceRef:hover {
  text-decoration: underline;
  text-decoration-color: var(--purple-light);
  text-underline-offset: 4px;
}

.sourceRef:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
  border-radius: 6px;
}

/* ── Disclosure (mirrors the Portal's <details> closure trail) ── */
.disclosure {
  border-radius: 12px;
}

.summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  padding: 4px 0;
  list-style: none;
}

.summary::-webkit-details-marker {
  display: none;
}

.summary::before {
  content: '▸';
  color: var(--gray-light);
  font-size: 0.8em;
  transition: transform 0.2s var(--ease);
}

.disclosure[open] .summary::before {
  transform: rotate(90deg);
}

.summaryText {
  flex: 1;
  font-family: var(--serif);
  font-weight: 500;
  font-size: 18px;
  color: var(--charcoal);
}

.count {
  font-size: 13px;
  color: var(--gray);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.summary:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
  border-radius: 6px;
}

/* ── Trail body ── */
.body {
  padding-top: 10px;
}

.trailList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* One consequence: the mock's .linked inset. */
.consequence {
  display: flex;
  align-items: center;
  gap: 6px 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 12px;
  background-color: var(--sage-light);
  font-size: 13.5px;
}

.relationship {
  font-weight: 700;
  color: var(--teal-dark);
}

.arrow {
  color: var(--gray-light);
}

.targetRef {
  font-weight: 600;
  color: var(--purple-dark);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

a.targetRef {
  text-decoration: underline;
  text-decoration-color: var(--purple-light);
  text-underline-offset: 3px;
}

a.targetRef:hover {
  text-decoration-color: var(--purple-dark);
}

.targetRef:focus-visible {
  outline: 2px solid var(--purple);
  outline-offset: 2px;
  border-radius: 6px;
}

.targetTitle {
  color: var(--charcoal);
}

.targetStatus {
  color: var(--gray);
  font-size: 12.5px;
}

/* ── Empty / loading / error ── */
.empty {
  margin: 0;
  max-width: 56ch;
  font-size: 14.5px;
  color: var(--gray);
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 0;
}

.loadingText {
  color: var(--gray);
  font-size: 14px;
}

.error {
  padding: 12px 16px;
  border-radius: 12px;
  background-color: var(--red-soft);
  color: var(--red-dark);
  font-size: 14px;
}
